import { eapUnclosedSessionsQuery } from "@/api/reporting";
import { DataTable } from "@/components/data-table/DataTable";
import { DataTableBody } from "@/components/data-table/DataTableBody";
import { StyledLink } from "@/components/link/StyledLink";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { isSessionOverdue, OverdueSessionPill } from "@/pages/admin/reports/eap/unclosedSessions";
import { formatDateTime } from "@common/utils/date";
import { Box, Text } from "@givenwell/components";
import { colors } from "@givenwell/theme";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/reports/$countryCode/eap-unclosed-sessions")({
  component: EapUnclosedSessionsPage,
  beforeLoad: () => ({
    breadcrumb: "Unclosed EAP Sessions",
  }),
});

/**
 * EAP sessions whose booking time has passed but which the provider has not yet closed off on their
 * Session Review screen. Payout is released by that review, so these sit as Confirmed with a Pending
 * payout until the provider reports on them.
 */
function EapUnclosedSessionsPage() {
  const sessionsQuery = useQuery(eapUnclosedSessionsQuery());

  return (
    <PageWrapper title="Unclosed EAP Sessions">
      <Box css={{ spaceY: 16, mt: 16 }}>
        <Text css={{ color: colors.text2 }}>
          Sessions that have taken place but have not been closed off by the provider. Each one links to the provider's
          Session Review screen.
        </Text>
        <DataTable>
          <DataTableBody
            rounded="full"
            data={sessionsQuery.data ?? []}
            status={sessionsQuery.status}
            columns={[
              {
                header: "Provider",
                cell: s => (
                  <StyledLink to="/admin/suppliers/$supplierId" params={{ supplierId: s.supplier_id }}>
                    {s.supplier_name}
                  </StyledLink>
                ),
              },
              {
                header: "Country",
                cell: s => s.country_code,
              },
              {
                header: "Session Date",
                cell: s => formatDateTime(s.booking_start),
              },
              {
                header: "",
                cell: s => (isSessionOverdue(s.booking_start) ? <OverdueSessionPill /> : null),
              },
              {
                header: "Purchase",
                cell: s => (
                  <StyledLink
                    to="/supplier/$supplierId/eap-review/$purchaseId"
                    params={{ supplierId: s.supplier_id, purchaseId: s.purchase_id }}
                  >
                    #{s.friendly_id}
                  </StyledLink>
                ),
              },
            ]}
            dataKey="purchase_id"
          />
        </DataTable>
      </Box>
    </PageWrapper>
  );
}
